import { computed, readonly } from 'vue'

export interface User {
  id: number
  email: string
  firstName: string | null
  lastName: string | null
  roles: string[]
  permissions: string[]
}

export const useUser = () => {
  // Nuxt composable state: shared across components
  const user = useState<User | null>('auth_user', () => null)
  const isAuthenticated = computed(() => !!user.value)

  // Must match server/utils/access.ts: only the integrations page, read-only, unless admin
  const isIntegrationsViewer = computed(() => {
    const roles = user.value?.roles || []
    return roles.includes('integrations_viewer') && !roles.includes('admin')
  })

  // Fetch current user from API
  const fetchUser = async () => {
    try {
      const userData = await $fetch<User>('/api/auth/me')
      user.value = userData
      return userData
    } catch (error) {
      user.value = null
      throw error
    }
  }

  // Logout
  const logout = async () => {
    // Clear user state first so UI updates immediately
    user.value = null
    
    try {
      // Call logout API to remove cookie
      await $fetch('/api/auth/logout', { method: 'POST' })
    } catch (error) {
      console.error('Logout error:', error)
      // Even if API call fails, user state is already cleared
    }
  }

  // Check if user has a specific permission
  const hasPermission = (permission: string): boolean => {
    if (!user.value) return false
    return user.value.permissions.includes(permission)
  }

  // Check if user has a specific role
  const hasRole = (role: string): boolean => {
    if (!user.value) return false
    return user.value.roles.includes(role)
  }

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissions: string[]): boolean => {
    if (!user.value) return false
    return permissions.some(perm => user.value!.permissions.includes(perm))
  }

  // Check if user has any of the specified roles
  const hasAnyRole = (roles: string[]): boolean => {
    if (!user.value) return false
    return roles.some(role => user.value!.roles.includes(role))
  }

  return {
    user: readonly(user),
    isAuthenticated,
    isIntegrationsViewer,
    fetchUser,
    logout,
    hasPermission,
    hasRole,
    hasAnyPermission,
    hasAnyRole
  }
}

